/**
 * Sessione in memoria: l'access token (15 min) non tocca mai lo storage del
 * browser; il refresh token vive in un cookie httpOnly gestito dall'API.
 */
import type { AuthResponse } from "@kipo/shared";

type Listener = (s: AuthResponse | null) => void;

let session: AuthResponse | null = null;
let refreshing: Promise<AuthResponse | null> | null = null;
const listeners = new Set<Listener>();

export function getSession() {
  return session;
}

export function setSession(s: AuthResponse | null) {
  session = s;
  listeners.forEach((l) => l(s));
}

export function onSession(l: Listener) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

async function post<T>(path: string, body?: unknown, bearer?: string): Promise<T> {
  const res = await fetch(`/api/auth${path}`, {
    method: "POST",
    credentials: "include",
    headers: { ...(body !== undefined ? { "content-type": "application/json" } : {}), ...(bearer ? { authorization: `Bearer ${bearer}` } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  if (!res.ok) throw new ApiError(res.status, json.error ?? "error", json.message ?? "Qualcosa non ha funzionato");
  return json as T;
}

/** Rinnova la sessione dal cookie (una sola richiesta in volo alla volta). */
export function refreshSession(tenantId?: string): Promise<AuthResponse | null> {
  refreshing ??= post<AuthResponse>("/refresh", tenantId ? { tenantId } : {})
    .then((s) => {
      setSession(s);
      return s;
    })
    .catch(() => {
      setSession(null);
      return null;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

export const auth = {
  async login(email: string, password: string) {
    setSession(await post<AuthResponse>("/login", { email, password }));
  },
  async register(input: { companyName: string; firstName: string; lastName: string; email: string; password: string; acceptTerms: true }) {
    setSession(await post<AuthResponse>("/register", input));
  },
  async logout() {
    await post("/logout").catch(() => {});
    setSession(null);
  },
  async switchTenant(tenantId: string) {
    setSession(await post<AuthResponse>("/switch-tenant", { tenantId }));
  },
  /** Console: entra come un utente del tenant per assistenza (un'ora al massimo). */
  async impersonate(input: { tenantId: string; userId?: string; reason: string }) {
    setSession(await post<AuthResponse>("/impersonate", input, session?.accessToken));
  },
  async stopImpersonation() {
    setSession(await post<AuthResponse>("/stop-impersonation"));
  },
  forgot: (email: string) => post("/forgot-password", { email }),
  reset: (token: string, password: string) => post("/reset-password", { token, password }),
  async acceptInvite(input: { token: string; firstName?: string; lastName?: string; password?: string }) {
    setSession(await post<AuthResponse>("/accept-invite", input));
  },
};

/** fetch autenticato: aggiunge il Bearer e, su 401, rinnova e riprova una volta. */
export async function authedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const withAuth = (token?: string): RequestInit => ({
    ...init,
    credentials: "include",
    headers: { ...(init.headers as Record<string, string>), ...(token ? { authorization: `Bearer ${token}` } : {}) },
  });
  let res = await fetch(input, withAuth(session?.accessToken));
  if (res.status === 401) {
    const s = await refreshSession();
    if (s) res = await fetch(input, withAuth(s.accessToken));
  }
  return res;
}
